import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, DollarSign, Clock, ShieldCheck, ChevronLeft, Gavel, Wallet,
  CheckCircle2, Star, TrendingUp, Users, Plane, ArrowRight,
} from "lucide-react";
import Logo from "@/components/Logo";
import Button from "@/components/Button";

const PERKS = [
  { icon: DollarSign, title: "Set your own fare", body: "Accept the recommended price or bid your own within the allowed range on every request." },
  { icon: Clock, title: "Drive on your schedule", body: "Go online whenever you want. No shifts, no minimums — you're in control." },
  { icon: Plane, title: "Airport-focused demand", body: "Steady MCO airport runs across Orlando's biggest resorts and hotels." },
];

const STEPS = [
  { icon: Gavel, title: "See requests & bid", body: "When you're online, nearby airport requests appear. Accept the recommended fare or name your own." },
  { icon: Users, title: "Riders pick you", body: "Riders compare offers and choose their driver. Get picked and the trip is yours." },
  { icon: Wallet, title: "Complete & get paid", body: "Drive the trip, complete it, and keep the fare plus 100% of your tips." },
];

const REQUIREMENTS = [
  "Be at least 21 years old",
  "Valid U.S. driver's license",
  "Vehicle model year 2010 or newer",
  "Current auto insurance & registration",
  "Pass a background & identity check",
  "Smartphone with data connection",
];

const STATS = [
  { value: "100%", label: "of tips are yours" },
  { value: "$0", label: "sign-up cost" },
  { value: "7 days", label: "advance bookings" },
  { value: "24/7", label: "airport demand" },
];

const FAQS = [
  { q: "How much can I earn?", a: "You set your own fares within a fair range for each trip, so your earnings scale with how much you drive and the prices you bid. You keep the full fare minus a small service fee, plus 100% of tips." },
  { q: "When do I get paid?", a: "Earnings from every completed trip are tracked in your Earnings tab, including a weekly breakdown and per-trip detail." },
  { q: "Do I need my own car?", a: "Yes. Your vehicle must be model year 2010 or newer and pass a basic condition check. Your category (Economy, SUV, Executive) is set automatically from your make and model." },
  { q: "How long does approval take?", a: "After you submit your application and documents, our team reviews it. You'll be able to go online as soon as your account is approved." },
];

export default function DriveWithUs() {
  const navigate = useNavigate();
  const apply = () => navigate("/auth?role=driver");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-card/90 px-5 py-3 backdrop-blur-md">
        <button onClick={() => navigate("/auth")} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">
          <ChevronLeft className="h-5 w-5" /> Back
        </button>
        <button onClick={() => navigate("/")} data-testid="logo-home"><Logo size={30} /></button>
        <Button size="sm" onClick={apply} data-testid="header-apply">Apply</Button>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-5 py-16 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-brand-700 dark:text-brand-400">
          <Car className="h-4 w-4" /> Drive with Getaride Orlando
        </span>
        <h1 className="mt-6 text-4xl font-extrabold leading-tight text-foreground sm:text-6xl">
          Earn on your terms.<br /><span className="text-brand-600">You name the fare.</span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
          Getaride is Orlando's airport-focused marketplace where riders choose you — and you choose your price.
        </p>
        <div className="mt-8 flex justify-center">
          <Button size="lg" onClick={apply} data-testid="apply-driver-cta">Apply to drive <ArrowRight className="h-5 w-5" /></Button>
        </div>
      </section>

      {/* Stats banner */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <div className="grid grid-cols-2 gap-4 rounded-3xl bg-gradient-to-br from-brand-600 to-brand-700 p-8 text-white sm:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-num text-3xl font-extrabold sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-sm text-white/85">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Perks */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <h2 className="mb-6 text-center text-3xl font-extrabold text-foreground">Why drivers love Getaride</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {PERKS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-6 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-brand-600"><p.icon className="h-6 w-6" /></span>
              <h3 className="mt-4 text-lg font-bold text-foreground">{p.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How you earn */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <h2 className="mb-6 text-center text-3xl font-extrabold text-foreground">How you earn</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 font-num text-lg font-bold text-white">{i + 1}</span>
                <s.icon className="h-6 w-6 text-brand-600" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-foreground">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements + earnings highlight */}
      <section className="mx-auto grid max-w-5xl gap-4 px-5 pb-16 md:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-8">
          <h2 className="text-2xl font-extrabold text-foreground">What you'll need</h2>
          <ul className="mt-5 space-y-3">
            {REQUIREMENTS.map((r) => (
              <li key={r} className="flex items-center gap-3 text-sm font-medium text-foreground">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-success" /> {r}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center rounded-3xl bg-gradient-to-br from-brand-600 to-brand-700 p-8 text-white">
          <TrendingUp className="h-10 w-10" />
          <h2 className="mt-4 text-2xl font-extrabold">Keep more of what you make</h2>
          <p className="mt-2 text-white/85">No surge games, no hidden cuts. Bid the fare that works for you, keep every tip, and watch your weekly earnings grow in real time.</p>
          <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-white/90">
            <ShieldCheck className="h-5 w-5" /> Transparent, driver-first payouts
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-3xl px-5 pb-16">
        <div className="rounded-3xl border border-border bg-card p-8 text-center">
          <div className="mb-3 flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />)}
          </div>
          <p className="text-lg font-semibold text-foreground">"I love that I set my own prices. Airport runs from the resorts keep me busy, and the tips come straight to me."</p>
          <p className="mt-3 text-sm text-muted-foreground">— Marcus R., Getaride driver in Orlando</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-16">
        <h2 className="mb-6 text-center text-3xl font-extrabold text-foreground">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
              <summary className="flex cursor-pointer list-none items-center justify-between font-bold text-foreground">
                {f.q}
                <ChevronLeft className="h-5 w-5 -rotate-90 text-muted-foreground transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-4xl px-5 pb-20 text-center">
        <div className="rounded-3xl border border-border bg-accent p-10">
          <h2 className="text-3xl font-extrabold text-foreground">Ready to start earning?</h2>
          <p className="mt-2 text-muted-foreground">Apply in minutes. Go online as soon as you're approved.</p>
          <div className="mt-6 flex justify-center">
            <Button size="lg" onClick={apply} data-testid="apply-driver-cta-bottom">Apply to drive <ArrowRight className="h-5 w-5" /></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
